/**
 * The whole voice-logging job, minus the buttons.
 *
 * Pulled out of the sheet because the microphone is no longer only in a sheet:
 * the Scan screen's shutter becomes the hold-to-talk button when the rail is on
 * "Balsu", and the review list is the only part that still needs to be a sheet.
 * Two copies of the parse-match-commit logic would drift within a week, so
 * there is one, and both callers render it.
 */

import { scaleToGrams } from '@gramas/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'
import type { LocalFood } from '../db/dexie.js'
import {
  foodDisplayName,
  foodPer100g,
  logAdHoc,
  logFood,
  searchFoodsLocal,
  slotFor,
} from '../db/repo.js'
import { logContext } from '../sass/context.js'
import { sassLine } from '../sass/engine.js'
import { maybeLogToast } from '../sass/toast.js'
import { syncNow } from '../sync/engine.js'
import { useRecorder, type VoiceClip } from './useRecorder.js'
import { useSpeech } from './useSpeech.js'

export interface ParsedItem {
  /** Assigned on arrival: the parser returns no ids and names repeat. */
  key: string
  /** What was heard, kept so a mismatched row can still say what you said. */
  heard: string
  grams: number
  /** null = heard, but matched nothing in the catalogue. */
  food: LocalFood | null
  candidates: LocalFood[]
}

interface WireItem {
  name: string
  quantity: number
  unit: 'g' | 'ml' | 'vnt'
  candidates: Array<{ food: LocalFood; similarity: number }>
}

export type VoicePhase =
  | { name: 'idle' }
  | { name: 'listening' }
  | { name: 'working' }
  | { name: 'review'; items: ParsedItem[] }
  | { name: 'failed'; message: string }

export function useVoiceLog(date: string, onLogged?: () => void) {
  const [lang, setLang] = useState<'lt' | 'en'>('lt')
  const [phase, setPhase] = useState<VoicePhase>({ name: 'idle' })
  const [saving, setSaving] = useState(false)

  /**
   * One parse, from either starting point.
   *
   * A transcript and a recording are the same request to the same route — what
   * differs is only which end of the listening the phone was able to do — so
   * everything downstream of this call, the candidate matching and the review
   * list included, has exactly one implementation.
   */
  /**
   * Which parse is still wanted.
   *
   * Bumped by `reset`, and checked after every await: leaving the screen while a
   * recording is being parsed used to let the answer arrive minutes later and
   * throw the review sheet over whatever the user had moved on to — the barcode
   * scanner, in the case that found it.
   */
  const runId = useRef(0)
  /** Whether a result from the microphone is still wanted. */
  const armed = useRef(false)

  const run = useCallback(
    async (input: { transcript: string } | { audio: VoiceClip }) => {
      const id = ++runId.current
      setPhase({ name: 'working' })
      try {
        const res = await api.post<{ items: WireItem[] }>('/api/ai/voice', { ...input, lang })
        const items = await Promise.all(
          res.items.map(async (item, index): Promise<ParsedItem> => {
            const remote = item.candidates.map((c) => c.food)
            // The server matches against the whole catalogue; the local search
            // covers anything cached offline that the server has not seen yet.
            const candidates = remote.length > 0 ? remote : await searchFoodsLocal(item.name, 5)
            return {
              key: `${index}-${item.name}`,
              heard: item.name,
              // A count of things is not a weight. Without a serving size for
              // the matched food there is nothing better than a nominal 100 g,
              // and the row is editable precisely because of cases like this.
              grams:
                item.unit === 'vnt'
                  ? (candidates[0]?.servingSizeG ?? 100) * item.quantity
                  : item.quantity,
              food: candidates[0] ?? null,
              candidates,
            }
          }),
        )
        if (runId.current !== id) return
        setPhase(
          items.length === 0
            ? {
                name: 'failed',
                message:
                  sassLine('error_voice_empty', {}) ?? 'Nieko neatpažinta. Bandykite dar kartą.',
              }
            : { name: 'review', items },
        )
      } catch {
        if (runId.current !== id) return
        setPhase({
          name: 'failed',
          message:
            sassLine('error_voice_network', {}) ?? 'Nepavyko susisiekti. Bandykite dar kartą.',
        })
      }
    },
    [lang],
  )

  // The typed-textarea path is a deliberate press of a button, so it parses
  // whatever the state of the microphone; the two engines only speak when the
  // press that started them has not since been abandoned.
  const parse = useCallback((transcript: string) => run({ transcript }), [run])
  const onHeard = useCallback(
    (transcript: string) => {
      if (!armed.current) return
      armed.current = false
      void run({ transcript })
    },
    [run],
  )
  const onClip = useCallback(
    (audio: VoiceClip) => {
      if (!armed.current) return
      armed.current = false
      void run({ audio })
    },
    [run],
  )

  const speech = useSpeech(lang, onHeard)
  const recorder = useRecorder(onClip)

  /**
   * The engine that can actually do the job here, behind one shape.
   *
   * Where the browser recognises the language itself that stays the fast path —
   * it costs nothing and answers before the button is back up. Where it does
   * not, which on an iPhone means always, the phone records instead and the
   * model on the server does the listening. Neither caller branches on this.
   */
  const usingRecorder = !speech.supported
  const mic = {
    supported: usingRecorder ? recorder.supported : true,
    // Which engine is *running* is not the same question as which one would be
    // chosen now: the language toggle sits directly above the hold button, and
    // flipping it mid-sentence used to re-point `stop` at the engine that was
    // not recording — leaving the other one to run to its cap with the
    // microphone lit and no way to put it down.
    listening: recorder.listening || speech.listening,
    error: recorder.error ?? speech.error,
    start: () => {
      armed.current = true
      if (usingRecorder) void recorder.start()
      else speech.start()
    },
    stop: () => {
      recorder.stop()
      speech.stop()
    },
  }

  const listening = mic.listening
  const micError = mic.error

  useEffect(() => {
    if (listening) setPhase({ name: 'listening' })
  }, [listening])

  // Nothing heard, or the microphone refused. Without this the sheet sat on
  // "Klausoma…" forever, because the phase only ever moved on a result.
  useEffect(() => {
    if (micError != null) setPhase((p) => (p.name === 'listening' ? { name: 'idle' } : p))
  }, [micError])

  // Held in a ref so `reset` can stay stable: every caller passes it to an
  // effect's dependency array.
  const discard = useRef<() => void>(() => {})
  discard.current = () => {
    recorder.cancel()
    speech.cancel()
  }

  /**
   * Put everything down: the microphone, the clip it was building, and any parse
   * still in flight. Not `stop`, which means "I have finished saying it" and
   * would upload the abandoned recording.
   */
  const reset = useCallback(() => {
    runId.current += 1
    armed.current = false
    discard.current()
    setPhase({ name: 'idle' })
  }, [])

  const patch = useCallback(
    (key: string, change: Partial<ParsedItem>) =>
      setPhase((p) =>
        p.name !== 'review'
          ? p
          : {
              name: 'review',
              items: p.items.map((i) => (i.key === key ? { ...i, ...change } : i)),
            },
      ),
    [],
  )

  const drop = useCallback(
    (key: string) =>
      setPhase((p) =>
        p.name !== 'review' ? p : { name: 'review', items: p.items.filter((i) => i.key !== key) },
      ),
    [],
  )

  const commitAll = useCallback(async () => {
    if (phase.name !== 'review' || saving) return
    setSaving(true)
    try {
      for (const item of phase.items) {
        // An amount that was cleared rather than corrected. A 0 g entry cannot
        // be rescaled later without dividing by zero, so it is never written.
        if (!(item.grams > 0)) continue
        if (item.food) {
          await logFood(item.food, {
            slot: slotFor(null, item.food.unit, foodDisplayName(item.food)),
            quantityG: item.grams,
            logDate: date,
          })
        } else {
          // Heard but unmatched. Recording it at zero keeps the day's list
          // honest about what was eaten while being obvious about what is
          // missing, which is better than dropping the row silently.
          await logAdHoc(
            item.heard,
            {
              kcal: 0,
              protein: 0,
              carbs: 0,
              fat: 0,
              sugars: null,
              satFat: null,
              fiber: null,
              salt: null,
            },
            {
              slot: slotFor(null, 'g', item.heard),
              quantityG: item.grams,
              logDate: date,
              notes: 'Neatpažinta — patikslinkite',
            },
          )
        }
      }
      void syncNow()
      setPhase({ name: 'idle' })
      onLogged?.()
      // One remark for the whole spoken batch — never one per item; the slot
      // of the first matched row stands in for a batch that may span several.
      const written = phase.items.filter((i) => i.grams > 0)
      if (written.length > 0) {
        const batchKcal = Math.round(
          written.reduce(
            (sum, i) => sum + (i.food ? scaleToGrams(foodPer100g(i.food), i.grams).kcal : 0),
            0,
          ),
        )
        const first = written[0]!
        const slot = first.food
          ? slotFor(null, first.food.unit, foodDisplayName(first.food))
          : undefined
        maybeLogToast({ ...(await logContext(date, slot, batchKcal)), count: written.length })
      }
    } finally {
      setSaving(false)
    }
  }, [phase, saving, date, onLogged])

  const total =
    phase.name === 'review'
      ? phase.items.reduce(
          (sum, i) => sum + (i.food ? scaleToGrams(foodPer100g(i.food), i.grams).kcal : 0),
          0,
        )
      : 0

  return { lang, setLang, phase, mic, parse, patch, drop, commitAll, reset, saving, total }
}

export type VoiceLog = ReturnType<typeof useVoiceLog>
